import { PROCUREMENT_SCENARIOS, MODE_NAMES } from './wizardData';

// Helper function to get the effective procurement type (handles mixed → primary purpose)
export const getEffectiveProcurementType = (wizardData) => {
  const originalType = wizardData.typeSelectionMethod === 'ai' 
    ? wizardData.aiRecommendedType 
    : wizardData.procurementType;
  
  // If mixed type and primary purpose is set (and not 'unable'), use primary purpose
  if (originalType === 'mixed' && wizardData.primaryPurpose && wizardData.primaryPurpose !== 'unable') {
    console.log('[getEffectiveProcurementType] Mixed type detected, returning primary purpose:', wizardData.primaryPurpose);
    return wizardData.primaryPurpose;
  }
  
  console.log('[getEffectiveProcurementType] Returning original type:', originalType);
  return originalType;
};

// Check thresholds from backend
export const checkThresholds = async (wizardData, setWizardData) => {
  const value = parseFloat(wizardData.contractValue.replace(/,/g, ''));
  if (isNaN(value) || value <= 0) return;
  
  try {
    const procType = wizardData.typeSelectionMethod === 'ai' 
      ? wizardData.aiRecommendedType 
      : wizardData.procurementType;
    
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/threshold-rules?` + 
      `entity_type=${encodeURIComponent(wizardData.entityType === 'national' || wizardData.entityType === 'gocc' || wizardData.entityType === 'suc' ? 'NGA/GOCC/SUC' : 'LGU')}&` +
      `procurement_type=${encodeURIComponent(procType.charAt(0).toUpperCase() + procType.slice(1))}&` +
      `contract_value=${value}`
    );
    
    if (response.ok) {
      const thresholds = await response.json();
      setWizardData(prev => ({
        ...prev,
        applicableThresholds: thresholds,
        suggestedModes: thresholds.map(t => t.mode_name).filter((v, i, a) => a.indexOf(v) === i)
      }));
    }
  } catch (error) {
    console.error('Threshold check error:', error);
  }
};

// Main ranking function with fixed matrix
export const rankModesWithFixedMatrix = (wizardData, setWizardData) => {
  console.log('[rankModesWithFixedMatrix] STARTING ranking...');
  setWizardData(prev => ({ ...prev, rankingLoading: true }));
  
  try {
    const procType = getEffectiveProcurementType(wizardData);
    const value = parseFloat(wizardData.contractValue.replace(/,/g, ''));
    console.log('[rankModesWithFixedMatrix] Proc Type:', procType, 'Value:', value);
    
    // Get scenarios for the specific procurement type
    const scenarios = PROCUREMENT_SCENARIOS[procType] || PROCUREMENT_SCENARIOS.goods;
    
    // Calculate scores for each unique mode based on matching scenarios
    const modeScores = {}; // { modeId: { score, matchedScenario, verify } }
    
    scenarios.forEach(scenario => {
      const modeId = scenario.modeId;
      
      // Check if this scenario is applicable based on ABC threshold
      let isApplicable = true;
      if (scenario.abcThreshold !== null && value > scenario.abcThreshold) {
        isApplicable = false;
      }
      
      // Special handling for specific modes with additional conditions
      
      // CSE from PS-DBM: Only for CSE items without exemption
      if (modeId === 'cse-psdbm') {
        if (wizardData.isCSE !== true) {
          isApplicable = false;
        } else {
          const hasJustification = Object.values(wizardData.cseJustification || {}).some(v => v);
          if (hasJustification) {
            isApplicable = false; // Exempt from centralized procurement
          }
        }
      }
      
      // Repeat Order: Only if actually replenishing (we assume applicable if selected)
      if (modeId === 'repeat-order' && procType !== 'goods') {
        isApplicable = false;
      }
      
      // Direct Sales: Only for non-CSE goods
      if (modeId === 'direct-sales') {
        if (procType !== 'goods' || wizardData.isCSE === true) {
          isApplicable = false;
        }
      }
      
      // Community Participation: Additional threshold checks
      if (modeId === 'community-participation') {
        if (procType === 'infrastructure') {
          if (wizardData.isCommunityBased !== true || value > 10000000) {
            isApplicable = false;
          }
        } else if (procType === 'goods') {
          // Multiple scenarios for goods with different thresholds
          if (scenario.abcThreshold && value > scenario.abcThreshold) {
            isApplicable = false;
          }
        }
      }
      
      // Highly Technical Consultants: Only for consulting
      if (modeId === 'highly-technical-consultants' && procType !== 'consulting') {
        isApplicable = false;
      }
      
      // Adjacent/Contiguous: Not for goods
      if (modeId === 'adjacent-contiguous' && procType === 'goods') {
        isApplicable = false;
      }
      
      // Direct Retail: Threshold check (₱1M or ₱5M with HoPE)
      if (modeId === 'direct-retail') {
        // We assume standard ₱1M limit
        if (value > 1000000) {
          isApplicable = false;
        }
      }
      
      if (!isApplicable) {
        return; // Skip this scenario
      }
      
      // Use base score from scenario
      let calculatedScore = scenario.score;
      
      // Store the highest score for each mode (some modes appear in multiple scenarios)
      if (!modeScores[modeId] || calculatedScore > modeScores[modeId].score) {
        modeScores[modeId] = {
          score: calculatedScore,
          matchedScenario: scenario.scenario,
          condition: scenario.condition,
          verify: scenario.verify,
          riskLevel: scenario.riskLevel
        };
      }
    });
    
    // Ensure Competitive Bidding is ALWAYS included
    if (!modeScores['competitive-bidding']) {
      const cbScenario = scenarios.find(s => s.modeId === 'competitive-bidding');
      if (cbScenario) {
        modeScores['competitive-bidding'] = {
          score: cbScenario.score,
          matchedScenario: cbScenario.scenario,
          condition: cbScenario.condition,
          verify: cbScenario.verify,
          riskLevel: cbScenario.riskLevel
        };
      }
    }
    
    // Apply special condition boosts
    applySpecialConditionBoosts(wizardData, modeScores, scenarios, procType, value);
    
    // Re-sort to include competitive bidding
    const rankedModeIds = Object.keys(modeScores).sort((a, b) => {
      return modeScores[b].score - modeScores[a].score;
    });
    
    // Create final ranked modes array with detailed info
    const rankedModes = rankedModeIds
      .filter(modeId => modeScores[modeId].score > 0) // Only include modes with positive scores
      .map((modeId, index) => ({
        rank: index + 1,
        id: modeId,
        name: MODE_NAMES[modeId] || modeId,
        score: Math.round(modeScores[modeId].score * 10) / 10, // Round to 1 decimal
        scenario: modeScores[modeId].matchedScenario,
        condition: modeScores[modeId].condition,
        verificationCriteria: modeScores[modeId].verify,
        riskLevel: modeScores[modeId].riskLevel,
        marketNote: modeScores[modeId].marketNote || null
      }));
    
    console.log('[Assessment Complete] Procurement Type:', procType);
    console.log('[Assessment Complete] Contract Value:', value);
    console.log('[Assessment Complete] Total Scenarios Evaluated:', scenarios.length);
    console.log('[Assessment Complete] Applicable Modes:', Object.keys(modeScores).length);
    console.log('[Assessment Complete] Ranked Modes:', rankedModes);
    if (rankedModes.length > 0) {
      console.log(`[Assessment Complete] Top Recommendation: ${rankedModes[0].name} (Score: ${rankedModes[0].score})`);
    }
    
    setWizardData(prev => ({ 
      ...prev, 
      rankedModes: rankedModes,
      rankingLoading: false 
    }));
  } catch (error) {
    console.error('Error in scenario-based assessment:', error);
    setWizardData(prev => ({ ...prev, rankingLoading: false }));
  }
};

// Apply special condition boosts based on user selections
function applySpecialConditionBoosts(wizardData, modeScores, scenarios, procType, value) {
  // BOOST modes if "None, no special condition" was selected
  if (wizardData.specialConditionChoice === 'none') {
    // Top 1: Competitive Bidding (always, regardless of value)
    if (modeScores['competitive-bidding']) {
      modeScores['competitive-bidding'].score = 100;
    }
    
    // Top 2: Small Value Procurement (if ABC ≤ ₱2,000,000) - for ALL types
    if (value <= 2000000) {
      if (modeScores['small-value']) {
        modeScores['small-value'].score = 95;
      } else {
        // Add SVP if it doesn't exist in scenarios (ensure it's always in top 3)
        const svpScenario = scenarios.find(s => s.modeId === 'small-value');
        if (svpScenario) {
          modeScores['small-value'] = {
            score: 95,
            matchedScenario: svpScenario.scenario,
            condition: svpScenario.condition,
            verify: svpScenario.verify,
            riskLevel: svpScenario.riskLevel
          };
        }
      }
    }
    
    // Top 3: Direct Acquisition for Goods only (if ABC ≤ ₱200,000)
    if (procType === 'goods' && value <= 200000) {
      if (modeScores['direct-acquisition']) {
        modeScores['direct-acquisition'].score = 90;
      } else {
        // Add Direct Acquisition if it doesn't exist
        const daScenario = scenarios.find(s => s.modeId === 'direct-acquisition');
        if (daScenario) {
          modeScores['direct-acquisition'] = {
            score: 90,
            matchedScenario: daScenario.scenario,
            condition: daScenario.condition,
            verify: daScenario.verify,
            riskLevel: daScenario.riskLevel
          };
        }
      }
    }
  }
  
  // ========== MARKET CONDITION ALIGNMENT LOGIC ==========
  // Apply market condition adjustments based on user's selection
  const marketCondition = wizardData.marketCondition;
  
  if (marketCondition) {
    // Competitive Bidding: preferred for robust/limited markets
    if (marketCondition === 'robust' || marketCondition === 'limited') {
      if (modeScores['competitive-bidding']) {
        modeScores['competitive-bidding'].score = Math.max(modeScores['competitive-bidding'].score, 98);
        modeScores['competitive-bidding'].marketNote = 'Preferred method for ' + marketCondition + ' market conditions';
      }
    }
    
    // Competitive Dialogue: for complex projects with robust/limited market
    if ((marketCondition === 'robust' || marketCondition === 'limited') && modeScores['competitive-dialogue']) {
      modeScores['competitive-dialogue'].score = Math.max(modeScores['competitive-dialogue'].score, 92);
      modeScores['competitive-dialogue'].marketNote = 'Suitable for complex projects with multiple capable players';
    }
    
    // Limited Source Bidding: specifically for limited markets with few known suppliers
    if (marketCondition === 'limited' && modeScores['limited-source-bidding']) {
      modeScores['limited-source-bidding'].score = Math.max(modeScores['limited-source-bidding'].score, 95);
      modeScores['limited-source-bidding'].marketNote = 'Appropriate when only a small number of known capable suppliers exist';
    }
    
    // Unsolicited Offer: needs at least one credible challenger (robust/limited)
    if ((marketCondition === 'robust' || marketCondition === 'limited') && modeScores['unsolicited']) {
      modeScores['unsolicited'].score = Math.max(modeScores['unsolicited'].score, 85);
      modeScores['unsolicited'].marketNote = 'Requires at least one credible challenger for bid matching';
    } else if (marketCondition === 'single' && modeScores['unsolicited']) {
      // Penalize unsolicited for single source - needs competition
      modeScores['unsolicited'].score = Math.min(modeScores['unsolicited'].score, 40);
      modeScores['unsolicited'].marketNote = '⚠️ Not recommended: requires credible challenger for bid matching';
    }
    
    // Direct Contracting: specifically for single source situations
    if (marketCondition === 'single' && modeScores['direct-contracting']) {
      modeScores['direct-contracting'].score = Math.max(modeScores['direct-contracting'].score, 98);
      modeScores['direct-contracting'].marketNote = 'Appropriate for proprietary, exclusive distributor, or critical components with single source';
    }
    
    // Small Value Procurement & Direct Acquisition: value-based, some competition preferred
    if (modeScores['small-value']) {
      if (marketCondition === 'robust' || marketCondition === 'limited') {
        modeScores['small-value'].marketNote = 'Value-based mode; competition can still be sought';
      }
    }
    if (modeScores['direct-acquisition']) {
      if (marketCondition === 'robust' || marketCondition === 'limited') {
        modeScores['direct-acquisition'].marketNote = 'Transaction-cost efficient; some competition can be sought';
      }
    }
    
    // Repeat Order: applicable regardless of current market condition (based on prior competitive process)
    if (modeScores['repeat-order']) {
      modeScores['repeat-order'].marketNote = 'Based on prior competitive process; market condition at re-order time less relevant';
    }
    
    // Negotiated Procurement: for specific RA 12009/IRR grounds, may work with limited/single source
    if ((marketCondition === 'limited' || marketCondition === 'single') && modeScores['negotiated']) {
      modeScores['negotiated'].score = Math.max(modeScores['negotiated'].score, 88);
      modeScores['negotiated'].marketNote = 'Applicable when specific RA 12009/IRR grounds are present (failed biddings, high specialization, emergencies)';
    }
    
    // Direct Sales: statutory conditions, may coincide with limited/single source
    if (modeScores['direct-sales']) {
      if (marketCondition === 'limited' || marketCondition === 'single') {
        modeScores['direct-sales'].marketNote = 'Follows specific statutory conditions';
      }
    }
    
    // STI (Direct Procurement for Science, Technology and Innovation): specific statutory conditions
    if (modeScores['sti']) {
      if (marketCondition === 'limited' || marketCondition === 'single') {
        modeScores['sti'].score = Math.max(modeScores['sti'].score, 90);
        modeScores['sti'].marketNote = 'Applicable under specific statutory conditions for innovation procurement';
      }
    }
    
    // Emergency: applicable regardless of market condition when grounds exist
    if (modeScores['emergency']) {
      modeScores['emergency'].marketNote = 'Applicable when emergency grounds exist, regardless of market condition';
    }
    
    // Agency-to-Agency: applicable for limited/single source when another agency has capacity
    if (modeScores['agency-to-agency']) {
      if (marketCondition === 'limited' || marketCondition === 'single') {
        modeScores['agency-to-agency'].marketNote = 'Suitable when another government agency has mandate and capacity; market condition less relevant';
      } else {
        modeScores['agency-to-agency'].marketNote = 'Consider when another government agency can provide requirements more efficiently';
      }
    }
    
    // NGO Participation: applicable when funds are earmarked for NGO
    if (modeScores['ngo-participation']) {
      modeScores['ngo-participation'].marketNote = 'Applicable when funds are earmarked for NGO by law or policy; market condition less relevant';
    }
    
    // UN Agencies: for specialized international requirements
    if (modeScores['un-agencies']) {
      if (marketCondition === 'limited' || marketCondition === 'single') {
        modeScores['un-agencies'].marketNote = 'Appropriate for specialized requirements aligned with international commitments';
      }
    }
    
    // Highly Technical Consultants: for specialized expertise
    if (modeScores['highly-technical-consultants']) {
      if (marketCondition === 'limited') {
        modeScores['highly-technical-consultants'].score = Math.max(modeScores['highly-technical-consultants'].score, 95);
        modeScores['highly-technical-consultants'].marketNote = 'Suitable when highly specialized expertise is required from limited available experts';
      } else if (marketCondition === 'single') {
        modeScores['highly-technical-consultants'].score = Math.max(modeScores['highly-technical-consultants'].score, 98);
        modeScores['highly-technical-consultants'].marketNote = 'Appropriate when only one expert possesses the required specialized knowledge';
      }
    }
    
    // Scientific/Scholarly/Artistic Work: for specialized projects
    if (modeScores['scientific-scholarly']) {
      if (marketCondition === 'limited' || marketCondition === 'single') {
        modeScores['scientific-scholarly'].score = Math.max(modeScores['scientific-scholarly'].score, 92);
        modeScores['scientific-scholarly'].marketNote = 'Applicable for projects requiring specialized scientific, scholarly, or artistic expertise';
      }
    }
    
    // Community Participation: applicable regardless of market for community-based projects
    if (modeScores['community-participation']) {
      modeScores['community-participation'].marketNote = 'For community-based projects; market condition less relevant as beneficiaries are local communities';
    }
    
    // Defense Cooperation: for defense-related requirements
    if (modeScores['defense-cooperation']) {
      modeScores['defense-cooperation'].marketNote = 'For defense and security requirements under government-to-government arrangements';
    }
    
    // Adjacent/Contiguous: for continuation of existing works
    if (modeScores['adjacent-contiguous']) {
      modeScores['adjacent-contiguous'].marketNote = 'For adjacent or contiguous works where continuity with existing contractor is advantageous';
    }
    
    // Take-over: for failed/terminated contracts
    if (modeScores['takeover']) {
      modeScores['takeover'].marketNote = 'For take-over of terminated or failed contracts; expedites project completion';
    }
  }
  
  // ========== GOODS SPECIAL CONDITIONS BOOSTING ==========
  
  // Condition 4: Repeat Order - Only show if selected
  if (wizardData.specialConditionChoice === 'goods-repeat' && modeScores['repeat-order']) {
    modeScores['repeat-order'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'goods-repeat') {
    delete modeScores['repeat-order']; // Hide if not selected
  }
  
  // Condition 5: Emergency - Make top 1 if selected, hide if not selected
  if (wizardData.specialConditionChoice === 'goods-emergency' && modeScores['emergency']) {
    modeScores['emergency'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'goods-emergency') {
    delete modeScores['emergency']; // Hide if not selected
  }
  
  // Condition 6: Direct Contracting (Proprietary)
  if (wizardData.specialConditionChoice === 'goods-proprietary' && modeScores['direct-contracting']) {
    modeScores['direct-contracting'].score = 100;
  }
  
  // Condition 7: From another entity with stocks - Sub-options
  if (wizardData.specialConditionChoice === 'goods-entity-stocks') {
    if (wizardData.specialConditionSubOption === 'goods-agency') {
      // Ensure Agency-to-Agency is #1 when selected
      if (modeScores['agency-to-agency']) {
        modeScores['agency-to-agency'].score = 100;
      } else {
        // Add if not present (shouldn't happen for goods, but safeguard)
        modeScores['agency-to-agency'] = {
          score: 100,
          matchedScenario: 'From another entity with stocks - Agency',
          condition: 'Another agency has capacity to provide/supply item',
          verify: 'Agency has legal mandate; capacity certification; no subcontracting',
          riskLevel: 'Medium'
        };
      }
    } else if (wizardData.specialConditionSubOption === 'goods-ngo') {
      // Add NGO Participation dynamically when selected
      modeScores['ngo-participation'] = {
        score: 100,
        matchedScenario: 'From another entity with stocks - NGO',
        condition: 'Fund earmarked for NGO by law, ordinance, or policy',
        verify: 'NGO has demonstrable capacity and track record; 3-day posting required; MOA and capability documentation required',
        riskLevel: 'Medium'
      };
    } else if (wizardData.specialConditionSubOption === 'goods-un-agencies') {
      // Ensure UN Agencies is #1 when selected
      if (modeScores['un-agencies']) {
        modeScores['un-agencies'].score = 100;
      } else {
        // Add if not present
        modeScores['un-agencies'] = {
          score: 100,
          matchedScenario: 'From another entity with stocks - UN/IO/IFI',
          condition: 'Specialized requirements aligned with international commitments',
          verify: 'Category verification; comparative advantage demonstrated',
          riskLevel: 'Medium'
        };
      }
    }
  }
  
  // Condition 8: Defense - Make top 1 if selected, hide if not selected
  if (wizardData.specialConditionChoice === 'goods-defense' && modeScores['defense-cooperation']) {
    modeScores['defense-cooperation'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'goods-defense') {
    delete modeScores['defense-cooperation']; // Hide if not selected
  }
  
  // Condition 9: Community Participation
  if (wizardData.specialConditionChoice === 'goods-community' && modeScores['community-participation']) {
    modeScores['community-participation'].score = 100;
  }
  
  // Condition 10: Sagip Saka
  if (wizardData.specialConditionChoice === 'goods-sagip-saka') {
    modeScores['sagip-saka'] = {
      score: 100,
      matchedScenario: 'Agricultural and fishery products under Sagip Saka Act',
      verify: 'Direct purchase of agricultural and fishery products from accredited farmers and fisherfolk cooperatives and enterprises under RA 11321 (Sagip Saka Act)',
      condition: 'Agricultural and fishery products necessary for agency operations',
      abcThreshold: null,
      riskLevel: 'Medium'
    };
  }
  
  // Condition 10.5: Negotiated Procurement for International Events, Conferences, Summits, or Activities Hosted by the Philippine Government
  if (wizardData.specialConditionChoice === 'goods-international-events') {
    modeScores['international-events'] = {
      score: 100,
      matchedScenario: 'Negotiated Procurement for International Events, Conferences, Summits, or Activities Hosted by the Philippine Government (e.g., ASEAN, APEC, SEA Games, UN meetings)',
      verify: 'Documentation of international event; justification of impracticability; value-for-money assessment; compliance with security and diplomatic protocols',
      condition: 'International event formally hosted by PH Government; existing modes impracticable due to security, protocol, or operational requirements',
      abcThreshold: null,
      riskLevel: 'Medium–High'
    };
  }
  
  // Condition 11: Direct Retail Purchase (POL, Electronic Charging Devices, Online Subscriptions ONLY)
  if (wizardData.specialConditionChoice === 'goods-pol') {
    // Ensure Direct Retail is #1 when selected
    if (modeScores['direct-retail']) {
      modeScores['direct-retail'].score = 100;
    } else {
      // Add if not present
      modeScores['direct-retail'] = {
        score: 100,
        matchedScenario: 'POL, Electronic Charging Devices, or Online Subscriptions ONLY',
        condition: 'Petroleum Fuel, Oil and Lubricant Products (POL), electronic charging devices, or online subscriptions needed for daily operations, not in PS-DBM. This mode is EXCLUSIVELY for these items only.',
        verify: 'Delegation papers; operational necessity; item must be POL, electronic charging device, or online subscription only',
        riskLevel: 'Medium'
      };
    }
  }
  
  // Condition 12: STI/R&D
  if (wizardData.specialConditionChoice === 'goods-rd' && modeScores['sti']) {
    modeScores['sti'].score = 100;
  }
  
  // Condition 13: Venue Rental/Lease
  if (wizardData.specialConditionChoice === 'goods-venue-rental') {
    modeScores['lease-venue'] = {
      score: 100,
      matchedScenario: 'Lease of real property, office space, facilities, or venue',
      verify: 'Government lease preferred; private if more economical. Limited available properties meet requirements. RFQs may yield one responsive quote; can proceed if with fair price.',
      condition: 'Lease of real property, office space, facilities, or venue for official government use',
      abcThreshold: null,
      riskLevel: 'Medium'
    };
  }

  // ========== INFRASTRUCTURE SPECIAL CONDITIONS BOOSTING ==========
  
  // Condition 1: National Security - Limited Source Bidding
  if (wizardData.specialConditionChoice === 'infra-national-security' && modeScores['limited-source-bidding']) {
    modeScores['limited-source-bidding'].score = 100;
  }
  
  // Condition 2: Complex Infrastructure - Competitive Dialogue top 1, Scientific top 2
  if (wizardData.specialConditionChoice === 'infra-complex') {
    if (modeScores['competitive-dialogue']) {
      modeScores['competitive-dialogue'].score = 100;
    }
    if (modeScores['scientific-scholarly']) {
      modeScores['scientific-scholarly'].score = 95; // Top 2
    }
  }
  
  // Condition 3: From another entity with capacity - Sub-options (INFRASTRUCTURE)
  if (wizardData.specialConditionChoice === 'infra-entity-capacity') {
    if (wizardData.specialConditionSubOption === 'infra-agency-sub') {
      // Ensure Agency-to-Agency is #1 when selected
      if (modeScores['agency-to-agency']) {
        modeScores['agency-to-agency'].score = 100;
      } else {
        // Add if not present
        modeScores['agency-to-agency'] = {
          score: 100,
          matchedScenario: 'From another entity with capacity - Agency',
          condition: 'Another agency has legal works mandate and track record',
          verify: 'Mandate; ≥50% project magnitude experience; absorptive capacity',
          riskLevel: 'Medium'
        };
      }
    } else if (wizardData.specialConditionSubOption === 'infra-ngo-sub') {
      // Add NGO Participation dynamically when selected for infrastructure
      modeScores['ngo-participation'] = {
        score: 100,
        matchedScenario: 'From another entity with capacity - NGO',
        condition: 'Fund earmarked for NGO by law, ordinance, or policy',
        verify: 'NGO has demonstrable capacity and track record; 3-day posting required; MOA and capability documentation required',
        riskLevel: 'Medium'
      };
    }
  }
  
  // Condition 4: Adjacent/Contiguous - Only show if selected, top 1 if selected
  if (wizardData.specialConditionChoice === 'infra-adjacent' && modeScores['adjacent-contiguous']) {
    modeScores['adjacent-contiguous'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'infra-adjacent') {
    // Only hide for infrastructure type
    if (procType === 'infrastructure') {
      delete modeScores['adjacent-contiguous'];
    }
  }
  
  // Condition 5: Emergency - Make top 1 if selected, hide if not selected
  if (wizardData.specialConditionChoice === 'infra-emergency' && modeScores['emergency']) {
    modeScores['emergency'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'infra-emergency') {
    // Only hide for infrastructure type
    if (procType === 'infrastructure') {
      delete modeScores['emergency'];
    }
  }
  
  // Condition 6: Heritage/Scientific - Top 1
  if (wizardData.specialConditionChoice === 'infra-heritage' && modeScores['scientific-scholarly']) {
    modeScores['scientific-scholarly'].score = 100;
  }
  
  // Condition 7: Community Participation - Top 1
  if (wizardData.specialConditionChoice === 'infra-community' && modeScores['community-participation']) {
    modeScores['community-participation'].score = 100;
  }
  
  // Condition 8: Take-over of Contracts - Only show if selected, top 1 if selected
  if (wizardData.specialConditionChoice === 'infra-takeover' && modeScores['takeover']) {
    modeScores['takeover'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'infra-takeover') {
    delete modeScores['takeover']; // Hide if not selected
  }
  
  // ========== CONSULTING SERVICES SPECIAL CONDITIONS BOOSTING ==========
  
  // Condition 3: Individual Expert - Highly Technical Consultants top 1
  if (wizardData.specialConditionChoice === 'consulting-expert' && modeScores['highly-technical-consultants']) {
    modeScores['highly-technical-consultants'].score = 100;
  }
  
  // Condition 4: Academic/Legal Research - Highly Technical #1, Scientific/Scholarly #2
  if (wizardData.specialConditionChoice === 'consulting-research') {
    if (modeScores['highly-technical-consultants']) {
      modeScores['highly-technical-consultants'].score = 100;
    }
    if (modeScores['scientific-scholarly']) {
      modeScores['scientific-scholarly'].score = 95; // Top 2
    }
  }
  
  // Condition 5 (NEW): Emergency - Make top 1 if selected, hide if not selected
  if (wizardData.specialConditionChoice === 'consulting-emergency' && modeScores['emergency']) {
    modeScores['emergency'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'consulting-emergency') {
    // Only hide for consulting type
    if (procType === 'consulting') {
      delete modeScores['emergency'];
    }
  }
  
  // Condition 6: From another entity with capacity - Sub-options
  if (wizardData.specialConditionChoice === 'consulting-entity-stocks') {
    if (wizardData.specialConditionSubOption === 'consulting-agency') {
      // Add or boost Agency-to-Agency dynamically when selected for consulting
      modeScores['agency-to-agency'] = {
        score: 100,
        matchedScenario: 'From another entity with capacity - Agency',
        condition: 'Another agency has capacity to provide consulting services',
        verify: 'Agency has legal mandate; technical capacity and track record; no subcontracting of main scope',
        riskLevel: 'Medium'
      };
    } else if (wizardData.specialConditionSubOption === 'consulting-ngo') {
      // Add NGO Participation dynamically when selected for consulting
      modeScores['ngo-participation'] = {
        score: 100,
        matchedScenario: 'From another entity with capacity - NGO',
        condition: 'Fund earmarked for NGO by law, ordinance, or policy',
        verify: 'NGO has demonstrable capacity and track record; 3-day posting required; MOA and capability documentation required',
        riskLevel: 'Medium'
      };
    } else if (wizardData.specialConditionSubOption === 'consulting-un-agencies') {
      // Add or boost UN Agencies dynamically when selected for consulting
      if (!modeScores['un-agencies']) {
        modeScores['un-agencies'] = {
          score: 100,
          matchedScenario: 'From another entity with capacity - UN/IO/IFI',
          condition: 'Specialized consulting from UN/IO/IFI teams',
          verify: 'Specialization documented; comparative advantage demonstrated; alignment with international commitments',
          riskLevel: 'Medium'
        };
      } else {
        modeScores['un-agencies'].score = 100;
      }
    }
  }
  
  // Condition 7: Defense Cooperation - Only show if selected, top 1 if selected
  if (wizardData.specialConditionChoice === 'consulting-defense' && modeScores['defense-cooperation']) {
    modeScores['defense-cooperation'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'consulting-defense') {
    // Only hide for consulting type
    if (procType === 'consulting') {
      delete modeScores['defense-cooperation'];
    }
  }
  
  // Condition 8: Adjacent/Contiguous (Continuation) - Only show if selected, top 1 if selected
  if (wizardData.specialConditionChoice === 'consulting-continuation' && modeScores['adjacent-contiguous']) {
    modeScores['adjacent-contiguous'].score = 100;
  } else if (wizardData.specialConditionChoice !== 'consulting-continuation') {
    // Only hide for consulting type
    if (procType === 'consulting') {
      delete modeScores['adjacent-contiguous'];
    }
  }
}

// Helper function to render consistent rank badges
export const renderRankBadge = (modeId, rankedModes) => {
  const modeRank = rankedModes.find(m => m.id === modeId);
  if (!modeRank) return null;
  
  const rank = modeRank.rank;
  const isTop3 = rank <= 3;
  const isNumber1 = rank === 1;
  const isNumber2 = rank === 2;
  const isNumber3 = rank === 3;
  
  // For top 3, show "Number 1", "Number 2", "Number 3" with special styling
  if (isTop3) {
    return (
      <div className={`absolute top-2 right-2 px-3 py-2 rounded-lg font-bold shadow-xl flex flex-col items-center justify-center ${
        isNumber1 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white border-2 border-yellow-300 animate-pulse' :
        isNumber2 ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-2 border-emerald-300' :
        'bg-gradient-to-br from-blue-500 to-blue-600 text-white border-2 border-blue-300'
      }`}>
        <div className="text-xs uppercase tracking-wide">Number</div>
        <div className="text-3xl font-extrabold leading-none">{rank}</div>
        {isNumber1 && <div className="text-[10px] mt-0.5 uppercase">Best Option</div>}
      </div>
    );
  }
  
  // For others, show simple "#4", "#5", etc.
  return (
    <div className="absolute top-2 right-2 bg-slate-500 text-white font-bold rounded-full w-10 h-10 flex items-center justify-center shadow-lg text-base">
      #{rank}
    </div>
  );
};
